import { useEffect, useRef, useState } from 'react';
import type { GenerationRequestDetailRead, SlideRead } from '../../types/api';
import { fetchGenerationRequest } from '../generation/generationApi';

/** Gap between detail refetches once the wait has settled in. */
const POLL_INTERVAL_MS = 1500;

/**
 * The first two gaps, before the steady cadence takes over.
 *
 * A re-render that finishes in 400ms used to sit on screen as a placeholder for
 * the rest of a flat 1.5s tick — the picture was ready and the UI was waiting on
 * a timer. Short gaps first cost at most two extra requests on a slow render and
 * buy back a second on every fast one.
 */
const POLL_RAMP_MS: readonly number[] = [400, 1000];

/** Gap before the attempt after `attempts` have already been made. */
function pollDelayMs(attempts: number): number {
  return POLL_RAMP_MS[attempts] ?? POLL_INTERVAL_MS;
}

/** ~59s of polling. Past this the wait is reported, not abandoned silently. */
const MAX_POLL_ATTEMPTS = 40;

export interface SlideRenderPollResult {
  /** The render has outlasted the attempt budget. Wording only, never an error. */
  isSlow: boolean;
}

/**
 * Identifies one wait: which slides are unrendered, and which version of them.
 *
 * Null when nothing is pending. A layer edit puts the same slide back into
 * rendering with a new `updated_at`, so that counts as a new wait and the slow
 * notice starts over instead of firing instantly on the second edit.
 */
export function pendingRenderKey(slides: readonly SlideRead[]): string | null {
  const pending = slides
    .filter((slide) => slide.rendered_path === null)
    .map((slide) => `${slide.id}:${slide.updated_at}`)
    .sort();
  return pending.length === 0 ? null : pending.join(',');
}

/**
 * Polls one request's detail while any of its slides is still being rendered.
 *
 * A slide's `rendered_path` is null while the design agent draws it — on the
 * first pass and again after every layer edit — and there is no SSE stage for a
 * single re-render, so this is a plain poll rather than a second stream.
 *
 * It stops the moment nothing is pending (`pendingKey` null), when the screen
 * unmounts, and after `MAX_POLL_ATTEMPTS`, where it reports a slow render rather
 * than hammering the API. The slow flag is stored against the wait it belongs
 * to, so it clears itself when the next render starts.
 */
export function useSlideRenderPolling(
  businessId: string | null,
  requestId: string | null,
  /** From {@link pendingRenderKey} — null means nothing is rendering. */
  pendingKey: string | null,
  onUpdate: (request: GenerationRequestDetailRead) => void,
): SlideRenderPollResult {
  const [slowKey, setSlowKey] = useState<string | null>(null);
  const updateRef = useRef(onUpdate);

  useEffect(() => {
    updateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (pendingKey === null || businessId === null || requestId === null) return;

    const business = businessId;
    const request = requestId;
    const wait = pendingKey;
    const controller = new AbortController();
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function schedule() {
      timer = setTimeout(() => {
        timer = null;
        void tick();
      }, pollDelayMs(attempts));
    }

    async function tick() {
      if (cancelled) return;
      attempts += 1;

      try {
        const detail = await fetchGenerationRequest(business, request, controller.signal);
        if (cancelled) return;
        // The parent re-derives the pending key from this; when the last slide
        // lands it goes null and this effect tears itself down.
        updateRef.current(detail);
      } catch {
        // A failed poll is not a failed render — keep waiting on the budget.
        if (cancelled) return;
      }

      if (attempts >= MAX_POLL_ATTEMPTS) {
        setSlowKey(wait);
        return;
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      controller.abort();
    };
  }, [businessId, pendingKey, requestId]);

  return { isSlow: pendingKey !== null && slowKey === pendingKey };
}
